import {getBatchResultsApi, getBatchStatusApi} from '../../api/enrichmentApi';
import {isLeadContactEnrichmentPayload} from './EnrichmentGrid.helpers';
import {batchCompletedNotice} from './aiEnrichmentNotices';
import {classifyLeadRow, LEAD_OUTCOME} from './leadOutcome';

// The backend can take BATCH_ITEM_TIMEOUT_SECONDS=90 per item; past this the client stops watching.
export const BATCH_WATCH_DEADLINE_MS = 15 * 60 * 1000;
const FIRST_POLL_MS = 3000;
const POLL_MS = 4000;
const RETRY_MS = 8000;
const MAX_POLL_RETRIES = 5;

// An accepted batch may still finish and charge, so stopping never claims failure or a refund.
export const stoppedWatchingCell = (saved) => (saved
  ? 'Still processing — results will appear in this saved search.'
  : 'Still processing — check company history for the result.');

const stoppedWatchingNotice = (saved) => ({
  open: true,
  severity: 'warning',
  message: `Stopped watching this run. It may still finish; ${saved
    ? 'results will appear in this saved search.' : 'check company history for results.'}`,
});

/** Settle every still-running cell of one grid action with `entry`. */
export function settleRunningEntries(statusMap, actionKey, companyIds, entry) {
  const cells = {...((statusMap || {})[actionKey] || {})};
  const running = companyIds.filter((id) => cells[id]?.status === 'running');
  if (!running.length) return statusMap;
  running.forEach((id) => { cells[id] = entry; });
  return {...statusMap, [actionKey]: cells};
}

const outcomeOf = (entry) => {
  if (entry.status === 'success') return 'ok';
  return entry.status === 'missing' ? 'none' : 'failed';
};

/**
 * Watch an accepted AI batch until it settles or the client stops watching.
 * Every path ends with no cell left running. Returns a cancel function for unmount.
 */
export function watchAiBatch({
  batchId, companyIds, saved, waitBeforeReveal, report, applyLeadColumns, markLeadRun, settleRunning, setSnackbar,
}) {
  const startedAt = Date.now();
  const outcomes = new Map(companyIds.map((id) => [id, 'pending']));
  const leadOutcomes = new Map();
  let timer = null;
  let cancelled = false;
  let lastCompleted = 0;
  let retries = 0;

  const settle = (id, entry) => {
    outcomes.set(id, outcomeOf(entry));
    report(id, entry);
  };
  const classifyLead = (id, derivedColumns) => {
    markLeadRun();
    settle(id, classifyLeadRow({leadOutcome: leadOutcomes.get(id), derivedColumns}));
  };
  const stopWatching = async () => {
    await waitBeforeReveal();
    settleRunning({status: 'missing', error: stoppedWatchingCell(saved)});
    setSnackbar(stoppedWatchingNotice(saved));
  };
  const applyItems = (items = []) => items.forEach((item) => {
    if (item.lead_outcome) leadOutcomes.set(item.company_id, item.lead_outcome);
    if (item.status === 'failed') settle(item.company_id, {status: 'failed', error: item.error || 'Failed'});
    else if (item.status === 'done' && item.lead_outcome && item.lead_outcome.outcome !== LEAD_OUTCOME.VERIFIED) {
      classifyLead(item.company_id);
    }
  });
  const applyResults = async () => {
    const results = (await getBatchResultsApi(batchId))?.data?.results || [];
    results.forEach((res) => {
      applyLeadColumns(res.company_id, res.derived_columns);
      if (leadOutcomes.has(res.company_id) || isLeadContactEnrichmentPayload(res)) {
        classifyLead(res.company_id, res.derived_columns);
        return;
      }
      const summary = res.answer?.answer?.summary || '';
      settle(res.company_id, summary ? {status: 'success', result: summary} : {status: 'failed', error: 'Empty AI response'});
    });
  };
  const finish = (batchStatus) => {
    const counts = {failed: 0, none: 0};
    outcomes.forEach((value) => {
      if (value === 'none') counts.none += 1;
      else if (value !== 'ok') counts.failed += 1;
    });
    if (batchStatus === 'completed') setSnackbar(batchCompletedNotice(counts.failed, saved, counts.none));
    else setSnackbar({open: true, message: `Batch finished with status: ${batchStatus}`, severity: 'warning'});
    settleRunning({status: 'failed', error: 'Item did not complete'});
  };
  const schedule = (ms) => {
    if (!cancelled) timer = setTimeout(poll, ms);
  };
  async function poll() {
    try {
      const status = (await getBatchStatusApi(batchId))?.data;
      retries = 0;
      if (cancelled) return;
      if (!status) {
        await stopWatching();
        return;
      }
      await waitBeforeReveal();
      applyItems(status.items);
      if (status.completed > lastCompleted) {
        await applyResults();
        lastCompleted = status.completed;
      }
      if (status.status !== 'pending' && status.status !== 'running') finish(status.status);
      else if (Date.now() - startedAt >= BATCH_WATCH_DEADLINE_MS) await stopWatching();
      else schedule(POLL_MS);
    } catch (pollError) {
      console.error('Polling error:', pollError);
      retries += 1;
      if (retries >= MAX_POLL_RETRIES) await stopWatching();
      else schedule(RETRY_MS);
    }
  }

  schedule(FIRST_POLL_MS);
  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}
